import axios from '../AxiosInstance';
import { getApiUrl } from '../../utils/ApiUtils';

/**
 * The momsdeklaration box register, served by the backend (G16).
 *
 * This replaced four hardcoded copies of the box labels — in `VatForm`,
 * `VatBoxDrilldownModal`, `ChartHealthSection` and `AccountPlanManager` — that
 * had drifted apart from each other and, in places, from Skatteverket. Fält 41
 * was labelled "Övrig försäljning utanför EU" in the account-plan picker, which
 * is a different transaction; fält 42 was "Momsfri försäljning" rather than
 * "Övrig försäljning med mera".
 *
 * Cached per browser session: the register changes when the law does, not per
 * tenant and not per render. In-flight requests are shared, so four components
 * mounting at once make one call.
 */
const cache = new Map();

export async function fetchVatMetadata(companyId) {
    if (!companyId) throw new Error('companyId krävs för att hämta momsmetadata.');
    if (cache.has(companyId)) return cache.get(companyId);

    const request = axios
        .get(getApiUrl(`${companyId}/reporting/vat-metadata`), { withCredentials: true })
        .then((response) => response.data.metadata)
        .catch((error) => {
            // A failed fetch must not poison the cache: the next mount retries.
            cache.delete(companyId);
            throw error;
        });

    cache.set(companyId, request);
    return request;
}

/** `{ '05': 'Momspliktig försäljning …' }` — the compact form, for tooltips and table headers. */
export function shortLabelsByBox(metadata) {
    return Object.fromEntries((metadata?.boxes || []).map((entry) => [entry.box, entry.short]));
}

/** `{ '05': 'Momspliktig försäljning som inte ingår i fält 06, 07 eller 08' }` — Skatteverket's own wording. */
export function officialLabelsByBox(metadata) {
    return Object.fromEntries((metadata?.boxes || []).map((entry) => [entry.box, entry.label]));
}

/** Select options for the boxes a reviewed allocation may target. */
export function allocationOptions(metadata) {
    return (metadata?.boxes || [])
        .filter((entry) => entry.allocatable && entry.kind === 'base')
        .map((entry) => ({ value: entry.box, label: `${entry.box} — ${entry.short}` }));
}

/** Select options for every box an account may be mapped to, plus "Ingen". */
export function mappingOptions(metadata, { includeEmpty = true } = {}) {
    const options = (metadata?.boxes || [])
        .filter((entry) => entry.kind !== 'derived')
        .map((entry) => ({ value: entry.box, label: `${entry.box} - ${entry.short}` }));
    return includeEmpty ? [{ value: '', label: 'Ingen' }, ...options] : options;
}
